import { useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Stack,
  Chip,
  TextField,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { HEADING_FONT } from "../theme/fonts";
import DashboardLayout from "../layouts/DashboardLayout";
import { useThemeColors } from "../context/ThemeContext";
import GlassCard from "../components/GlassCard";
import { glassRowSx } from "../components/glassRow";
import { AuthContext } from "../context/AuthContext";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import TableSkeleton from "../components/TableSkeleton";
import api from "../api";
import { useSessions } from "../hooks/queries";

function isExpired(expiresAt) {
  if (!expiresAt) return false;
  const exp = new Date(expiresAt.replace(" ", "T"));
  return exp.getTime() < Date.now();
}

function formatRelative(dateStr) {
  if (!dateStr) return "—";
  const then = new Date(dateStr.replace(" ", "T"));
  if (isNaN(then.getTime())) return dateStr;
  const diffMs = Date.now() - then.getTime();
  const past = diffMs >= 0;
  const abs = Math.abs(diffMs);
  const diffMin = Math.floor(abs / 60000);
  if (diffMin < 1) return past ? "just now" : "in <1m";
  if (diffMin < 60) return past ? `${diffMin}m ago` : `in ${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return past ? `${diffH}h ago` : `in ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return past ? `${diffD}d ago` : `in ${diffD}d`;
  const diffMo = Math.floor(diffD / 30);
  return past ? `${diffMo}mo ago` : `in ${diffMo}mo`;
}

function formatAbsolute(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.replace(" ", "T"));
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Turn the /sessions response into { rows, total, counts } for the current page.
// The backend normally returns { rows, total, counts }; if an older build
// returns a bare array, we filter/paginate + tally on the client (using the
// userLookup + active filters) so the page still works during a partial deploy.
const EMPTY_COUNTS = { all: 0, active: 0, expired: 0 };

function normalizeSessions(data, page, pageSize, filter, debouncedSearch, userLookup) {
  if (!data) return { rows: [], total: 0, counts: EMPTY_COUNTS };
  if (Array.isArray(data)) {
    const q = debouncedSearch.trim().toLowerCase();
    const matched = data.filter((s) => {
      const userName = (userLookup.get(s.adminId) || "").toLowerCase();
      const matchesSearch =
        !q ||
        userName.includes(q) ||
        (s.ipAddress || "").toLowerCase().includes(q);
      const expired = isExpired(s.expiresAt);
      const matchesFilter =
        filter === "all" ||
        (filter === "active" && !expired) ||
        (filter === "expired" && expired);
      return matchesSearch && matchesFilter;
    });
    const tally = { all: data.length, active: 0, expired: 0 };
    data.forEach((s) => {
      if (isExpired(s.expiresAt)) tally.expired++;
      else tally.active++;
    });
    const start = (page - 1) * pageSize;
    return {
      rows: matched.slice(start, start + pageSize),
      total: matched.length,
      counts: tally,
    };
  }
  return {
    rows: data.rows || [],
    total: data.total || 0,
    counts: data.counts || EMPTY_COUNTS,
  };
}

function Sessions() {
  const colors = useThemeColors();
  const { hasPermission } = useContext(AuthContext);
  const canDelete = hasPermission("sessions", "delete");
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [userLookup, setUserLookup] = useState(new Map());
  const [search, setSearch] = useState("");
  // Debounced copy of `search` so we hit the API after the user stops typing,
  // not on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Build the query params from the active page + filters. React Query
  // re-fetches automatically whenever this object changes — and serves the
  // cache when we revisit the same combination.
  const params = useMemo(() => {
    const p = { limit: pageSize, offset: (page - 1) * pageSize };
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    if (filter !== "all") p.status = filter;
    return p;
  }, [page, pageSize, debouncedSearch, filter]);

  // placeholderData keeps the previous page on screen while the next one loads,
  // so paging/filtering never flashes an empty table. The server returns
  // { rows, total, counts }; normalizeSessions also handles a bare-array
  // response by filtering/paginating on the client.
  const sessionsQuery = useSessions(params, { placeholderData: (prev) => prev });
  const { rows, total, counts } = useMemo(
    () =>
      normalizeSessions(
        sessionsQuery.data,
        page,
        pageSize,
        filter,
        debouncedSearch,
        userLookup
      ),
    [sessionsQuery.data, page, pageSize, filter, debouncedSearch, userLookup]
  );
  const loading = sessionsQuery.isFetching;

  // Surface a load failure as a toast (matches the old behaviour).
  useEffect(() => {
    if (sessionsQuery.isError) {
      toast.error(sessionsQuery.error?.message || "Failed to load sessions");
    }
  }, [sessionsQuery.isError, sessionsQuery.error, toast]);

  // Load the user lookup once so usernames render (search is now server-side,
  // but the table still maps adminId → username from this map).
  useEffect(() => {
    api
      .get("/users")
      .then((userRows) =>
        setUserLookup(
          new Map(userRows.map((u) => [u.id, u.username || `User #${u.id}`]))
        )
      )
      .catch(() => {
        // Non-fatal — rows fall back to "User #<id>".
      });
  }, []);

  // Debounce the search box → debouncedSearch.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  async function handleRevoke(id) {
    try {
      await api.delete(`/sessions/${id}`);
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      toast.info("Session revoked");
    } catch (err) {
      toast.error(err.message || "Failed to revoke session");
    }
  }

  // Reset to page 1 when the (debounced) search, filter, or page size changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  // If the current page falls past the end (e.g. after revoking the last row on
  // the last page), step back so we never show an empty page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = (safePage - 1) * pageSize;
  const rangeStart = total === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + pageSize, total);

  function buildPageList(current, total) {
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...pages]
      .filter((n) => n >= 1 && n <= total)
      .sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of sorted) {
      if (n - prev > 1) out.push("...");
      out.push(n);
      prev = n;
    }
    return out;
  }
  const pageList = buildPageList(safePage, totalPages);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#818cf8" },
    },
    "& input": { color: colors.text },
  };

  const GRID_COLUMNS =
    "minmax(160px, 1.2fr) minmax(180px, 1.4fr) 130px minmax(140px, 1fr) minmax(140px, 1fr) 100px 130px";

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Sessions
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textMuted, mt: 0.5 }}
          >
            {counts.all} active or expired login session
            {counts.all === 1 ? "" : "s"} · revoke any token to log it out
          </Typography>
        </Box>
      </Stack>

      {/* Filter + search */}
      <GlassCard
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { md: "center" },
        }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, value) => value && setFilter(value)}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              px: 2,
              py: 0.75,
              border: `1px solid ${colors.cardBorder}`,
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
              "&.Mui-selected": {
                background: "rgba(79, 70, 229, 0.12)",
                color: "#818cf8",
                borderColor: "#818cf8",
                "&:hover": { background: "rgba(79, 70, 229, 0.18)" },
              },
            },
          }}
        >
          <ToggleButton value="all">
            All
            <Chip
              size="small"
              label={counts.all}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: colors.hover,
                color: colors.textMuted,
              }}
            />
          </ToggleButton>
          <ToggleButton value="active">
            Active
            <Chip
              size="small"
              label={counts.active}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(16, 185, 129, 0.15)",
                color: "#10b981",
              }}
            />
          </ToggleButton>
          <ToggleButton value="expired">
            Expired
            <Chip
              size="small"
              label={counts.expired}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
              }}
            />
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search by user, IP or token..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            ...fieldSx,
            flex: 1,
            "& .MuiOutlinedInput-root": {
              ...fieldSx["& .MuiOutlinedInput-root"],
              fontSize: 13,
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ color: colors.textFaint, mr: 1, fontSize: 18 }}
              />
            ),
          }}
        />
      </GlassCard>

      {/* Empty state */}
      {rows.length === 0 && loading ? (
        <GlassCard sx={{ overflow: "hidden" }}>
          <TableSkeleton rows={pageSize} columns={7} />
        </GlassCard>
      ) : rows.length === 0 ? (
        <GlassCard sx={{ py: 8, textAlign: "center" }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#818cf8",
              background: "rgba(129, 140, 248, 0.12)",
              border: "1px solid rgba(129, 140, 248, 0.25)",
            }}
          >
            <VpnKeyOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: 16,
              fontWeight: 700,
              color: colors.text,
              mb: 0.5,
            }}
          >
            {counts.all === 0 ? "No sessions yet" : "No matches found"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
            {counts.all === 0
              ? "Sessions will appear here when users log in"
              : "Try a different search or filter"}
          </Typography>
        </GlassCard>
      ) : (
        <GlassCard sx={{ overflow: "hidden" }}>
          {/* Horizontal scroll wrapper — full table on mobile, swipe sideways */}
          <Box sx={{ overflowX: "auto" }}>
          {/* Table header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: GRID_COLUMNS,
              minWidth: { xs: "max-content", md: "auto" },
              gap: 2,
              px: 3,
              py: 1.75,
              borderBottom: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
              fontSize: 12,
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <Box sx={{ fontFamily: HEADING_FONT }}>User</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Token</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Status</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>IP Address</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Device</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Created</Box>
            <Box sx={{ fontFamily: HEADING_FONT, textAlign: "center" }}>Actions</Box>
          </Box>

          {/* Rows */}
          {rows.map((session, idx) => {
            const expired = isExpired(session.expiresAt);
            const statusColor = expired ? "#ef4444" : "#10b981";
            const statusBg = expired
              ? "rgba(239, 68, 68, 0.12)"
              : "rgba(16, 185, 129, 0.12)";
            const statusGradient = expired
              ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
              : "linear-gradient(135deg, #10b981 0%, #059669 100%)";
            const userName =
              userLookup.get(session.adminId) ||
              `User #${session.adminId}`;
            const initial = userName.charAt(0).toUpperCase();

            return (
              <Box
                key={session.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: GRID_COLUMNS,
                  minWidth: { xs: "max-content", md: "auto" },
                  gap: 2,
                  px: 3,
                  py: 2,
                  alignItems: "center",
                  borderBottom: `1px solid ${colors.divider}`,
                  "&:last-child": { borderBottom: "none" },
                  ...glassRowSx({ idx, accent: statusGradient, hoverBg: colors.hover }),
                }}
              >
                {/* User */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    {initial}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontSize: 14,
                        fontWeight: 600,
                        color: colors.text,
                      }}
                    >
                      {userName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: colors.textMuted,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                      }}
                    >
                      Session #{session.id}
                    </Typography>
                  </Box>
                </Stack>

                {/* Token — hidden for security; only revocation is exposed */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    sx={{
                      fontSize: 12,
                      color: colors.textMuted,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                      background: colors.hover,
                      border: `1px solid ${colors.cardBorder}`,
                      borderRadius: 1,
                      px: 1,
                      py: 0.4,
                      fontWeight: 500,
                    }}
                  >
                    •••• hidden
                  </Box>
                </Stack>

                {/* Status */}
                <Chip
                  size="small"
                  icon={
                    expired ? (
                      <ErrorOutlineOutlinedIcon
                        sx={{
                          fontSize: 14,
                          color: `${statusColor} !important`,
                        }}
                      />
                    ) : (
                      <CheckCircleOutlinedIcon
                        sx={{
                          fontSize: 14,
                          color: `${statusColor} !important`,
                        }}
                      />
                    )
                  }
                  label={expired ? "Expired" : "Active"}
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontWeight: 700,
                    fontSize: 11,
                    color: statusColor,
                    background: statusBg,
                    border: `1px solid ${statusColor}33`,
                    width: "fit-content",
                    "& .MuiChip-icon": { ml: "6px" },
                  }}
                />

                {/* IP Address */}
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <PublicOutlinedIcon
                    sx={{ fontSize: 14, color: colors.textMuted }}
                  />
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: colors.text,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {session.ipAddress || "—"}
                  </Typography>
                </Stack>

                {/* Device */}
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <LaptopMacOutlinedIcon
                    sx={{ fontSize: 14, color: colors.textMuted }}
                  />
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontSize: 12,
                      color: session.deviceInfo
                        ? colors.text
                        : colors.textFaint,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {session.deviceInfo || "Unknown"}
                  </Typography>
                </Stack>

                {/* Created (with expiry sub-line) */}
                <Box>
                  <Tooltip title={formatAbsolute(session.createdAt)}>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontSize: 12,
                        color: colors.text,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatRelative(session.createdAt)}
                    </Typography>
                  </Tooltip>
                  <Tooltip title={`Expires: ${formatAbsolute(session.expiresAt)}`}>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontSize: 11,
                        color: expired ? "#ef4444" : colors.textMuted,
                        whiteSpace: "nowrap",
                        mt: 0.25,
                      }}
                    >
                      exp {formatRelative(session.expiresAt)}
                    </Typography>
                  </Tooltip>
                </Box>

                {/* Actions */}
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                >
                  {canDelete && (
                    <Button
                      size="small"
                      startIcon={
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      }
                      onClick={async () => {
                        if (
                          await confirm({
                            title: "Revoke Session?",
                            message:
                              "The user will be logged out and the token will no longer work.",
                            danger: true,
                            confirmLabel: "Revoke",
                          })
                        )
                          handleRevoke(session.id);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12,
                        px: 1.25,
                        py: 0.4,
                        minWidth: 0,
                        borderRadius: 1.5,
                        color: "#ef4444",
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1px solid rgba(239, 68, 68, 0.25)",
                        "&:hover": {
                          background: "rgba(239, 68, 68, 0.18)",
                        },
                      }}
                    >
                      Revoke
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })}
          </Box>

          {/* Pagination footer — sessions */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderTop: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                Showing{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: colors.text }}
                >
                  {rangeStart}–{rangeEnd}
                </Box>{" "}
                of{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: colors.text }}
                >
                  {total}
                </Box>
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                MenuProps={{
                  anchorOrigin: { vertical: "top", horizontal: "left" },
                  transformOrigin: { vertical: "bottom", horizontal: "left" },
                  PaperProps: {
                    sx: {
                      mb: 0.5,
                      minWidth: 72,
                      borderRadius: 2,
                      background: colors.menuBg,
                      border: `1px solid ${colors.cardBorder}`,
                      boxShadow: colors.shadowLg,
                      "& .MuiList-root": { py: 0.5 },
                      "& .MuiMenuItem-root": {
                        fontSize: 13,
                        color: colors.text,
                        mx: 0.5,
                        my: 0.25,
                        minHeight: 32,
                        borderRadius: 1,
                        justifyContent: "center",
                        "&:hover": { background: colors.hover },
                        "&.Mui-selected": {
                          background: "rgba(129, 140, 248, 0.14)",
                          color: "#818cf8",
                          fontWeight: 700,
                          "&:hover": { background: "rgba(129, 140, 248, 0.2)" },
                        },
                      },
                    },
                  },
                }}
                sx={{
                  mr: 0.5,
                  height: 32,
                  minWidth: 62,
                  fontSize: 13,
                  fontWeight: 700,
                  color: colors.text,
                  background: colors.cardBg,
                  borderRadius: 2,
                  transition: "border-color 0.15s ease, background 0.15s ease",
                  "& .MuiSelect-select": {
                    py: 0,
                    pl: 1.5,
                    pr: "28px !important",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorder,
                  },
                  "&:hover": { background: colors.hover },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorderHover,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#818cf8",
                    borderWidth: "1px",
                  },
                  "& .MuiSvgIcon-root": { color: colors.textMuted, right: 4 },
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>
                    {n}
                  </MenuItem>
                ))}
              </Select>

              <Tooltip title="First page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => setPage(1)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        background: colors.cardBg,
                        color: "#818cf8",
                      },
                    }}
                  >
                    <FirstPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        background: colors.cardBg,
                        color: "#818cf8",
                      },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {pageList.map((p, i) =>
                p === "..." ? (
                  <Box
                    key={`ellipsis-${i}`}
                    sx={{
                      px: 1,
                      fontSize: 13,
                      color: colors.textFaint,
                    }}
                  >
                    …
                  </Box>
                ) : (
                  <Box
                    key={p}
                    component="button"
                    onClick={() => setPage(p)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      px: 1,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor:
                        p === safePage ? "#818cf8" : "transparent",
                      background:
                        p === safePage
                          ? "rgba(79, 70, 229, 0.12)"
                          : "transparent",
                      color: p === safePage ? "#818cf8" : colors.text,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background:
                          p === safePage
                            ? "rgba(79, 70, 229, 0.18)"
                            : colors.cardBg,
                      },
                    }}
                  >
                    {p}
                  </Box>
                )
              )}

              <Tooltip title="Next">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        background: colors.cardBg,
                        color: "#818cf8",
                      },
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Last page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() => setPage(totalPages)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        background: colors.cardBg,
                        color: "#818cf8",
                      },
                    }}
                  >
                    <LastPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </GlassCard>
      )}

    </DashboardLayout>
  );
}

export default Sessions;
